import { useState, useEffect, useMemo } from "react";
import { X, Loader2, AlertTriangle, Plus, Trash2, ArrowRight, Search, Check } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("adminToken") || "";
const headers = () => ({ Authorization: `Bearer ${getToken()}` });

// Generate one idempotency key per modal session
function useIdempotencyKey() {
  return useMemo(() => `tconv_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`, []);
}

export function TrialConversionModal({ lead, onClose, onConverted }) {
  const idempotencyKey = useIdempotencyKey();

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("customer"); // customer | products | confirm
  const [submitting, setSubmitting] = useState(false);

  // Customer
  const [customerMode, setCustomerMode] = useState("auto"); // auto | new
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: lead?.name || "",
    email: lead?.email || "",
    phone: lead?.phone || "",
    billing_address: "",
    shipping_address: "",
  });
  const [customerDupes, setCustomerDupes] = useState([]);
  const [forceNewCustomer, setForceNewCustomer] = useState(false);
  const [sendPasswordSetup, setSendPasswordSetup] = useState(true);

  // Products
  const [products, setProducts] = useState([{ outfit_id: "", rental_price: 0, deposit_amount: 0, discount_amount: 0 }]);
  const [outfitOptions, setOutfitOptions] = useState([]);

  // Booking details
  const [details, setDetails] = useState({
    rental_start_date: lead?.preferred_date || "",
    return_date: "",
    rental_days: 1,
    delivery_method: "pickup",
    delivery_address: "",
    postcode: "",
    suburb: "",
    delivery_fee: 0,
    pickup_appointment_date: "",
    pickup_appointment_time: "",
    return_appointment_date: "",
    return_appointment_time: "",
    payment_status_manual: "unpaid",
    internal_payment_notes: "",
  });

  // Initial load: preview + outfits + customer suggestion
  useEffect(() => {
    if (!lead?.id) return;
    (async () => {
      try {
        const [pRes, oRes] = await Promise.all([
          axios.get(`${API}/admin/trial-leads/${lead.id}/conversion-preview`, { headers: headers() }),
          axios.get(`${API}/outfits?available=true&limit=500`),
        ]);
        setPreview(pRes.data);
        setOutfitOptions(oRes.data.outfits || oRes.data || []);
        if (pRes.data?.suggested_customer) {
          setSelectedCustomerId(pRes.data.suggested_customer.customer_id || pRes.data.suggested_customer.id);
          setCustomerMode("auto");
        } else {
          setCustomerMode("new");
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to load lead preview");
        onClose?.();
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id]);

  // Customer search
  useEffect(() => {
    if (customerMode !== "auto" || !searchQ.trim()) {
      setSearchResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await axios.get(`${API}/admin/customers?q=${encodeURIComponent(searchQ)}&limit=10`, { headers: headers() });
        setSearchResults(r.data.customers || []);
      } catch {}
      setSearching(false);
    }, 250);
    return () => clearTimeout(t);
  }, [searchQ, customerMode]);

  // Auto-derive return date
  useEffect(() => {
    if (details.rental_start_date && details.rental_days > 0 && !details.return_date) {
      const d = new Date(details.rental_start_date);
      d.setDate(d.getDate() + Number(details.rental_days));
      setDetails((s) => ({ ...s, return_date: d.toISOString().slice(0, 10) }));
    }
  }, [details.rental_start_date, details.rental_days, details.return_date]);

  const totalPrice = useMemo(() => {
    return products.reduce((sum, p) =>
      sum + (Number(p.rental_price) || 0) + (Number(p.deposit_amount) || 0) - (Number(p.discount_amount) || 0)
    , 0) + (Number(details.delivery_fee) || 0);
  }, [products, details.delivery_fee]);

  const canProceed = () => {
    if (step === "customer") {
      if (customerMode === "auto") return !!selectedCustomerId;
      return newCustomer.name.trim() && newCustomer.email.includes("@");
    }
    if (step === "products") {
      return products.every(p => p.outfit_id) && details.rental_start_date && details.return_date;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        idempotency_key: idempotencyKey,
        ...(customerMode === "auto"
          ? { customer_id: selectedCustomerId }
          : { new_customer: newCustomer, customer_force: forceNewCustomer }),
        products,
        ...details,
        send_password_setup: customerMode === "new" && sendPasswordSetup,
      };
      const r = await axios.post(`${API}/admin/trial-leads/${lead.id}/convert`, payload, { headers: headers() });

      if (r.data?.duplicate) {
        setCustomerDupes(r.data.matches || []);
        setStep("customer");
        toast.warning("Duplicate customer detected — pick one or confirm 'create anyway'");
        setSubmitting(false);
        return;
      }

      toast.success(`Trial converted → booking ${r.data.first_booking_id}`);
      onConverted?.(r.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "object" && detail?.products) {
        toast.error(`Unavailable: ${detail.products.map(p => p.outfit_id + " — " + p.reason).join("; ")}`);
      } else {
        toast.error(detail || "Conversion failed");
      }
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <Wrapper onClose={onClose}>
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#9A7B4F]" /></div>
      </Wrapper>
    );
  }

  if (preview?.already_converted) {
    return (
      <Wrapper onClose={onClose} title="Already Converted">
        <div className="text-center py-6">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <p className="text-[13px] text-[#282828] mb-2">This trial has already been converted.</p>
          <p className="text-[11px] text-[#676664]/60">Booking ID: <span className="font-mono">{preview.converted_booking_id}</span></p>
          <Button onClick={onClose} className="mt-4 bg-[#9A7B4F] hover:bg-[#8B6B3F]">Close</Button>
        </div>
      </Wrapper>
    );
  }

  if (!preview?.can_convert) {
    return (
      <Wrapper onClose={onClose} title="Not Eligible">
        <div className="text-center py-6">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <p className="text-[13px] text-[#282828] mb-2">This lead can't be converted yet.</p>
          <p className="text-[11px] text-[#676664]/60">Current status: <strong>{preview?.current_status}</strong></p>
          <p className="text-[11px] text-[#676664]/60 mt-1">Lead must be marked <strong>contacted</strong> or <strong>booked</strong> first.</p>
          <Button onClick={onClose} className="mt-4 bg-[#9A7B4F] hover:bg-[#8B6B3F]">Close</Button>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper onClose={onClose} title="Convert Trial → Rental Booking">
      {/* Steps */}
      <div className="flex items-center gap-2 mb-5 text-[11px] uppercase tracking-wider">
        {["customer", "products", "confirm"].map((s, i) => (
          <div key={s} className={`flex items-center gap-2 ${step === s ? "text-[#9A7B4F]" : "text-[#676664]/40"}`}>
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${step === s ? "bg-[#9A7B4F] text-white" : "bg-[#FAF8F0] border border-[#282828]/10"}`}>{i + 1}</span>
            {s}
            {i < 2 && <ArrowRight className="w-3 h-3 text-[#676664]/30" />}
          </div>
        ))}
      </div>

      {/* Step 1: Customer */}
      {step === "customer" && (
        <div className="space-y-4" data-testid="step-customer">
          <div className="flex gap-2 text-[11px] uppercase tracking-wider">
            <button onClick={() => setCustomerMode("auto")} className={`px-3 py-1.5 border ${customerMode === "auto" ? "bg-[#282828] text-white border-[#282828]" : "border-[#282828]/10 text-[#676664]"}`} data-testid="mode-existing">Existing</button>
            <button onClick={() => setCustomerMode("new")} className={`px-3 py-1.5 border ${customerMode === "new" ? "bg-[#282828] text-white border-[#282828]" : "border-[#282828]/10 text-[#676664]"}`} data-testid="mode-new">Create New</button>
          </div>

          {customerMode === "auto" && (
            <div>
              {preview?.suggested_customer && (
                <div className="bg-emerald-50 border border-emerald-200 p-3 mb-3 text-[12px]">
                  <div className="text-emerald-700 font-medium mb-1 text-[11px] uppercase tracking-wider">Suggested match (by email/phone)</div>
                  <SelectableCustomer
                    customer={preview.suggested_customer}
                    selected={selectedCustomerId === (preview.suggested_customer.customer_id || preview.suggested_customer.id)}
                    onClick={() => setSelectedCustomerId(preview.suggested_customer.customer_id || preview.suggested_customer.id)}
                  />
                </div>
              )}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#676664]/40" />
                <Input
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  placeholder="Search by name/email/phone…"
                  className="pl-10 text-[13px]"
                  data-testid="customer-search-input"
                />
              </div>
              {searching && <Loader2 className="w-4 h-4 animate-spin text-[#9A7B4F] my-2" />}
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {searchResults.map((c) => (
                  <SelectableCustomer
                    key={c.customer_id}
                    customer={c}
                    selected={selectedCustomerId === c.customer_id}
                    onClick={() => setSelectedCustomerId(c.customer_id)}
                  />
                ))}
              </div>
            </div>
          )}

          {customerMode === "new" && (
            <div className="space-y-3">
              {customerDupes.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 p-3" data-testid="dupe-warn-modal">
                  <div className="text-[11px] uppercase tracking-wider text-amber-700 mb-2 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Possible duplicate
                  </div>
                  {customerDupes.map((m) => (
                    <SelectableCustomer
                      key={m.customer_id}
                      customer={m}
                      onClick={() => { setCustomerMode("auto"); setSelectedCustomerId(m.customer_id); setCustomerDupes([]); }}
                    />
                  ))}
                  <label className="flex items-center gap-1.5 mt-3 text-[11px] text-amber-900">
                    <input type="checkbox" checked={forceNewCustomer} onChange={(e) => setForceNewCustomer(e.target.checked)} data-testid="force-new-customer" />
                    Create new anyway
                  </label>
                </div>
              )}
              <Input value={newCustomer.name} onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })} placeholder="Full name *" data-testid="new-customer-name" />
              <Input type="email" value={newCustomer.email} onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })} placeholder="Email *" data-testid="new-customer-email" />
              <Input value={newCustomer.phone} onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })} placeholder="Phone (04xx xxx xxx)" data-testid="new-customer-phone" />
              <label className="flex items-center gap-1.5 text-[11px] text-[#676664]">
                <input type="checkbox" checked={sendPasswordSetup} onChange={(e) => setSendPasswordSetup(e.target.checked)} data-testid="send-pw-setup" />
                Email a password setup link after conversion
              </label>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Products + dates + payment */}
      {step === "products" && (
        <div className="space-y-4" data-testid="step-products">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#676664]/60 mb-2">Products</div>
            {products.map((p, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center" data-testid={`product-row-${i}`}>
                <select
                  value={p.outfit_id}
                  onChange={(e) => {
                    const next = [...products];
                    next[i].outfit_id = e.target.value;
                    const o = outfitOptions.find((o) => o.id === e.target.value);
                    if (o) {
                      next[i].rental_price = o.rental_price || o.price || 0;
                      next[i].deposit_amount = o.bond_amount || 0;
                    }
                    setProducts(next);
                  }}
                  className="col-span-5 border border-[#282828]/12 px-2 py-1.5 text-[12px]"
                  data-testid={`product-select-${i}`}
                >
                  <option value="">— Select outfit —</option>
                  {outfitOptions.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
                <Input type="number" step="0.01" value={p.rental_price} onChange={(e) => { const n = [...products]; n[i].rental_price = e.target.value; setProducts(n); }} placeholder="Price" className="col-span-2 text-[12px]" />
                <Input type="number" step="0.01" value={p.deposit_amount} onChange={(e) => { const n = [...products]; n[i].deposit_amount = e.target.value; setProducts(n); }} placeholder="Bond" className="col-span-2 text-[12px]" />
                <Input type="number" step="0.01" value={p.discount_amount} onChange={(e) => { const n = [...products]; n[i].discount_amount = e.target.value; setProducts(n); }} placeholder="Disc" className="col-span-2 text-[12px]" />
                <button onClick={() => setProducts(products.filter((_, ix) => ix !== i))} className="col-span-1 text-red-500/60 hover:text-red-600" disabled={products.length === 1}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <button onClick={() => setProducts([...products, { outfit_id: "", rental_price: 0, deposit_amount: 0, discount_amount: 0 }])} className="text-[11px] text-[#9A7B4F] inline-flex items-center gap-1 mt-1" data-testid="add-product-btn">
              <Plus className="w-3 h-3" /> Add product
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3 text-[12px]">
            <Labeled label="Rental Start *">
              <Input type="date" value={details.rental_start_date} onChange={(e) => setDetails({ ...details, rental_start_date: e.target.value })} data-testid="rental-start" />
            </Labeled>
            <Labeled label="Rental Days">
              <Input type="number" min={1} value={details.rental_days} onChange={(e) => setDetails({ ...details, rental_days: parseInt(e.target.value || 1) })} />
            </Labeled>
            <Labeled label="Return Date *">
              <Input type="date" value={details.return_date} onChange={(e) => setDetails({ ...details, return_date: e.target.value })} data-testid="return-date" />
            </Labeled>
            <Labeled label="Delivery Method">
              <select value={details.delivery_method} onChange={(e) => setDetails({ ...details, delivery_method: e.target.value })} className="w-full border border-[#282828]/12 px-2 py-1.5 text-[12px]">
                <option value="pickup">Pickup</option>
                <option value="delivery">Delivery</option>
              </select>
            </Labeled>
            {details.delivery_method === "delivery" && (
              <>
                <Labeled label="Delivery Fee"><Input type="number" step="0.01" value={details.delivery_fee} onChange={(e) => setDetails({ ...details, delivery_fee: e.target.value })} /></Labeled>
                <Labeled label="Postcode"><Input value={details.postcode} onChange={(e) => setDetails({ ...details, postcode: e.target.value })} /></Labeled>
                <Labeled label="Delivery Address" full><Input value={details.delivery_address} onChange={(e) => setDetails({ ...details, delivery_address: e.target.value })} /></Labeled>
              </>
            )}
            <Labeled label="Pickup Appt. Date"><Input type="date" value={details.pickup_appointment_date} onChange={(e) => setDetails({ ...details, pickup_appointment_date: e.target.value })} /></Labeled>
            <Labeled label="Pickup Appt. Time"><Input type="time" value={details.pickup_appointment_time} onChange={(e) => setDetails({ ...details, pickup_appointment_time: e.target.value })} /></Labeled>
            <Labeled label="Return Appt. Date"><Input type="date" value={details.return_appointment_date} onChange={(e) => setDetails({ ...details, return_appointment_date: e.target.value })} /></Labeled>
            <Labeled label="Return Appt. Time"><Input type="time" value={details.return_appointment_time} onChange={(e) => setDetails({ ...details, return_appointment_time: e.target.value })} /></Labeled>
            <Labeled label="Payment Status">
              <select value={details.payment_status_manual} onChange={(e) => setDetails({ ...details, payment_status_manual: e.target.value })} className="w-full border border-[#282828]/12 px-2 py-1.5 text-[12px]" data-testid="payment-status">
                <option value="unpaid">Unpaid</option>
                <option value="paid_manual">Paid (manually)</option>
                <option value="partial">Partial</option>
              </select>
            </Labeled>
            <Labeled label="Payment Notes" full>
              <Input value={details.internal_payment_notes} onChange={(e) => setDetails({ ...details, internal_payment_notes: e.target.value })} placeholder="Internal notes" />
            </Labeled>
          </div>
        </div>
      )}

      {/* Step 3: Confirm */}
      {step === "confirm" && (
        <div className="space-y-3 text-[12px]" data-testid="step-confirm">
          <div className="bg-[#FAF8F0] p-4 border border-[#282828]/8">
            <div className="text-[10px] uppercase tracking-wider text-[#676664]/60 mb-2">Summary</div>
            <div className="space-y-1.5">
              <SummaryRow label="Lead" value={lead.name + " · " + lead.email} />
              <SummaryRow label="Customer" value={customerMode === "new" ? `New: ${newCustomer.name}` : `Existing: ${selectedCustomerId}`} />
              <SummaryRow label="Products" value={`${products.length} item(s)`} />
              <SummaryRow label="Dates" value={`${details.rental_start_date} → ${details.return_date}`} />
              <SummaryRow label="Payment" value={details.payment_status_manual} />
              <SummaryRow label="Total" value={`$${totalPrice.toFixed(2)}`} bold />
            </div>
          </div>
          <p className="text-[11px] text-[#676664]/60">Clicking <strong>Confirm Conversion</strong> will create the booking(s), mark the trial as converted, and fire confirmation emails. Idempotency key prevents duplicate bookings on accidental double-clicks.</p>
        </div>
      )}

      {/* Footer */}
      <div className="flex justify-between items-center pt-4 mt-4 border-t border-[#282828]/8">
        <button onClick={() => {
          if (step === "products") setStep("customer");
          else if (step === "confirm") setStep("products");
          else onClose();
        }} className="text-[11px] text-[#676664]/60 hover:text-[#282828] uppercase tracking-wider" data-testid="modal-back-btn">
          {step === "customer" ? "Cancel" : "← Back"}
        </button>
        {step !== "confirm" ? (
          <Button
            onClick={() => setStep(step === "customer" ? "products" : "confirm")}
            disabled={!canProceed()}
            className="bg-[#9A7B4F] hover:bg-[#8B6B3F]"
            data-testid="modal-next-btn"
          >
            Next <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={submitting} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="confirm-conversion-btn">
            {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
            Confirm Conversion
          </Button>
        )}
      </div>
    </Wrapper>
  );
}

function Wrapper({ onClose, children, title }) {
  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="trial-conversion-modal">
      <div className="bg-white max-w-2xl w-full p-6 border border-[#282828]/8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[18px] text-[#282828]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>{title || "Convert Trial"}</h3>
          <button onClick={onClose} className="p-1 text-[#676664]/40 hover:text-[#282828]"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function SelectableCustomer({ customer, selected, onClick }) {
  return (
    <button onClick={onClick} className={`w-full text-left px-3 py-2 border ${selected ? "border-[#9A7B4F] bg-[#9A7B4F]/5" : "border-[#282828]/10 hover:bg-[#FAF8F0]"}`} data-testid={`customer-option-${customer.customer_id || customer.id}`}>
      <div className="text-[12px] text-[#282828]">{customer.name || "(no name)"} <span className="text-[#676664]/60">— {customer.email}</span></div>
      {customer.phone && <div className="text-[10px] text-[#676664]/60">{customer.phone}</div>}
    </button>
  );
}

function Labeled({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="block text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">{label}</label>
      {children}
    </div>
  );
}

function SummaryRow({ label, value, bold }) {
  return (
    <div className="flex justify-between text-[12px]">
      <span className="text-[#676664]/60">{label}</span>
      <span className={bold ? "text-[#282828] font-semibold" : "text-[#282828]"}>{value}</span>
    </div>
  );
}
